import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HOME_ID, NAV_ITEMS } from '@/shared/routes.ts';
import useTheme from '@/hooks/useTheme';
import useLanguage from '@/hooks/useLanguage';
import styles from './Header.module.scss';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'sk', label: 'SK' }
] as const;

interface Props {
  hideNav?: boolean;
  onLogoClick?: () => void;
}

const Header = ({ hideNav = false, onLogoClick }: Props) => {
  const { t } = useTranslation();
  const { toggle } = useTheme();
  const { language, setLanguage } = useLanguage();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const nextLang = LANGUAGES.find((l) => l.code !== language) ?? LANGUAGES[0];

  return (
    <header className={`${styles.header}${scrolled ? ` ${styles.scrolled}` : ''}`}>
      <a
        href={onLogoClick ? '/' : `#${HOME_ID}`}
        className={styles.logo}
        onClick={(e) => {
          e.preventDefault();
          if (onLogoClick) {
            onLogoClick();
          } else {
            document.getElementById(HOME_ID)?.scrollIntoView({ behavior: 'smooth' });
            closeMenu();
          }
        }}
      >
        {t('header.logo.before')}
        <span className={styles.logoDev}>{t('header.logo.dev')}</span>
      </a>
      {hideNav ? (
        <span aria-hidden='true' />
      ) : (
        <nav className={`${styles.nav}${menuOpen ? ` ${styles.navOpen}` : ''}`} aria-label='Main navigation'>
          {NAV_ITEMS.map(({ id }) => (
            <a
              key={id}
              href={`#${id}`}
              className={styles.navLink}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                closeMenu();
              }}
            >
              {t(`header.nav.${id}`)}
            </a>
          ))}
        </nav>
      )}
      <div className={styles.actions}>
        <button
          type='button'
          className={styles.langToggle}
          onClick={() => setLanguage(nextLang.code)}
          aria-label={t('header.toggleLang')}
        >
          {nextLang.label}
        </button>
        <button type='button' className={styles.themeToggle} onClick={toggle} aria-label={t('header.toggleTheme')}>
          <svg className={styles.sunIcon} aria-hidden='true'>
            <use href='/icons.svg#sun-icon' />
          </svg>
          <svg className={styles.moonIcon} aria-hidden='true'>
            <use href='/icons.svg#moon-icon' />
          </svg>
        </button>
        {!hideNav && (
          <button
            type='button'
            className={styles.menuToggle}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? t('header.closeMenu') : t('header.openMenu')}
            aria-expanded={menuOpen}
          >
            <svg aria-hidden='true'>
              <use href={menuOpen ? '/icons.svg#x-close' : '/icons.svg#menu'} />
            </svg>
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
